import React from 'react';
import {connect} from 'react-redux';
import ReactTable from 'react-table';
import selectTableHOC from 'react-table/lib/hoc/selectTable';
import _ from 'lodash';
import moment from "moment";
import Modal from 'react-responsive-modal';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

import GLOBAL_FILTER_CONTROLLER from '../../controllers/globalFilter';
import ERROR_DOCUMENT_DETAIL_CONTROLLER from '../../controllers/errorDocumentDetail';
import ERRORSWARNINGS_CONTROLLER from '../../controllers/errorsWarnings';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import NoData from '../page/NoData';
import PaginationComponent from '../page/Pagination'
import { ErrorWarningsCategory, ErrorWarningAction, ErrorKind, FeatureFlagEnum, FilingTypeEnum } from '../../utils/enums';
import ActionsPanel from './ActionsPanel';
import ExportPanel from './ExportPanel';
import BulkReplacePanel from './BulkReplacePanel';
import ErrorDetailsModal from './ErrorDetailsModal';
import ApplicationMessages from '../inAppMessages/ApplicationMessages';
import StaticApplicationMessage from '../inAppMessages/StaticApplicationMessage';
import {ErrorsWarningsConfig} from '../../utils/appConfig';
import DisabledActions from './DisabledActions';
import ReschedulePanel from './correctives/ReschedulePanel';
import ReadOnlyWrapper from '../authorization/ReadOnlyWrapper';

import Logger, { LOG_LEVELS, ALL_MESSAGES } from '../../utils/logger';
import {getNonCorrectiveTypes, rescheduleAllowedForReturnType} from "../../utils/featureFlagMappingForReturnType";

const FORM_NAME = 'EXISTING_ERRORS_WARNINGS';
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);
const INVALID_RESCHEDULE_PERIOD_STATUS = "InvalidReschedulePeriod";

const SelectTable = selectTableHOC(ReactTable);

// Separate component for checkbox input to avoid inline component definition
const SelectInputComponent = (type) => {
    return (
        <ReadOnlyWrapper>
            <fieldset>
                <input
                    className='checkbox absolute'
                    type={'checkbox'}
                    checked={type.checked}
                    onClick={(e)=>{
                      const { shiftKey } = e;
                      e.stopPropagation();
                      type.onClick(type.id, shiftKey, type.row);
                    }}
                    onChange={()=>{}}
                />
                <label className="ml15 checkbox"></label>
            </fieldset>
        </ReadOnlyWrapper>
    );
};

const RESCHEDULE_MODAL_CLASSNAMES = {
    modal: 'modal-full-width mt150',
};

export const ROW_ACTIONS = {
    VIEW_AND_RESOLVE: 'VIEW_AND_RESOLVE'
};

const STANDARD_MODAL_CLASSNAMES = {
    modal: 'modal-full-width',
};

const  DEFAULT_ERROR_DISABLE_ACTIONS = [
    ErrorWarningAction.ERROR_ACTION_MOVE_WARNING_TO_ERROR.value
];

const DEFAULT_WARNING_DISABLE_ACTIONS = [
    ErrorWarningAction.ERROR_ACTION_SAVE_TO_FILE.value,
    ErrorWarningAction.BULK_FIND_AND_REPLACE.value
];

const NoDataComponent = (props) => {
    return (
        <NoData noDataText="errorAndWarnings_NoData" {...props} />
    );
};

const CREDIT_NOTE_FF = FeatureFlagEnum.ENABLE_ENTER_CREDIT_NOTE_CERTIFICATE_DATE;

const ERROR_ID_CLOSED_FILING = 58;
const ERROR_ID_CREDIT_NOTE_CERTIFICATE = 60;

const shouldShowMaxRecordWarning = (isSelectAll, totalItemsCount, errorId, filingType, featureFlags) => {
  switch (true) {
    case !isSelectAll:
    case totalItemsCount < ErrorsWarningsConfig.MAX_PROCESSING_LIMIT:
    case errorId === ERROR_ID_CREDIT_NOTE_CERTIFICATE:
    case (errorId === ERROR_ID_CLOSED_FILING && rescheduleAllowedForReturnType(filingType, featureFlags)):
      return false;
    default:
      return true;
  }
};

// reduce cognitive complexity, keep Sonar happy :-)
const valueOrDefault = (returnValue, defaultValue) => returnValue || defaultValue;

const getMessageHeaderRenderer = (languages, subCategoryData) => {
  return () => (
    <div>
      <div className="inline-block">
        {LANGUAGES_CONTROLLER.format(languages, 'existingErrorsWarnings_All')}
      </div>
      <div className='ml20 inline-block' data-testid="sub-type-search-value">
        {`${subCategoryData?.subTypeSearchValue ? subCategoryData.subTypeSearchValue : ''}`}
      </div>
      <div className='ml20 inline-block' data-testid="message-total-items-count">
        {`${subCategoryData?.totalItemsCount ? subCategoryData.totalItemsCount : ''}`}
      </div>
    </div>
  );
};

const getMessageRowRenderer = (languages) => {
  return (row) => (
    <div>
      <span className="uppercase">
        {LANGUAGES_CONTROLLER.format(languages, 'existingErrorsWarnings_Invoice')}
      </span> - <span className="bold mr30" data-testid="message-invoice-number">{`${row.original ? row.original.invoiceNumber : ''}`}</span>
      <span>{`${row.value}`}</span>
    </div>
  );
};

const getActionsRowRenderer = (errorKind, actionClickHandler, languages) => {
  return (row) => {
    let original = valueOrDefault(row.original, {});

    if (errorKind === ErrorKind.ERROR) {
      return (
        <a className="float-right mr20" data-testid="details-link" onClick={actionClickHandler(ROW_ACTIONS.VIEW_AND_RESOLVE, row)}>
          <span>{LANGUAGES_CONTROLLER.format(languages, 'existingErrorsWarnings_Details')}</span>
        </a>
      );
    }

    if (`${original.documentId}` !== '0') {
      return (
        <span className="float-right mr20">
          <Link to={'/TransactionSearch?exactMatch&reference=' + original.documentId + '&searchType=1'}>
            {LANGUAGES_CONTROLLER.format(languages, 'existingErrorsWarnings_Details')}
          </Link>
        </span>
      );
    }

    return (
      <span className="float-right mr20">
        <span
          data-tip={LANGUAGES_CONTROLLER.format(languages, 'errorsAndWarnings_warningAlsoError')}
          className='info'
        >
          {LANGUAGES_CONTROLLER.format(languages, 'existingErrorsWarnings_Details')}
          <ReactTooltip className='info'/>
        </span>
      </span>
    );
  };
};

const maxRecordsMessage = (show, languages) => {
  if (!show) {
    return null;
  }

  return (
    <StaticApplicationMessage
      messageType="applicationWarning"
      string="errorsWarningsPage_MaxRecordsLimitWarning"
      languages={languages}
      params={[ErrorsWarningsConfig.MAX_PROCESSING_LIMIT]}
    />
  );
};

const summaryCountry = (countryCode, languages) => {
  if (_.isEmpty(countryCode)) {
    return null;
  }

  return (
    <>
      <span className="uppercase">
        {LANGUAGES_CONTROLLER.format(languages, 'exisitngErrorsWarnings_Country')}:
      </span>
      <span className="bold">{countryCode}</span>
    </>
  );
};

const summaryPeriod = (filterCriteria, languages) => {
  const {startDate, endDate, countryCode} = filterCriteria;

  if (_.isEmpty(startDate)) {
    return null;
  }

  return (
    <>
      {!_.isEmpty(countryCode) && <span className="divider-left margin-left-sm margin-right-sm"></span>}
      <span className="uppercase">
        {LANGUAGES_CONTROLLER.format(languages, 'exisitngErrorsWarnings_Period')}:
      </span>
      <span className="bold">{startDate} - {endDate}</span>
    </>
  );
};

const summaryInvoiceNumber = (filterCriteria, languages) => {
  const {invoiceNumberFilter, startDate} = filterCriteria;

  if (_.isEmpty(invoiceNumberFilter)) {
    return null;
  }

  return (
    <>
      {!_.isEmpty(startDate) && <span className="divider-left margin-left-sm margin-right-sm"></span>}
      <span className="uppercase">
        {LANGUAGES_CONTROLLER.format(languages, 'exisitngErrorsWarnings_InvoiceNumber')}:
      </span>
      <span className="bold">{invoiceNumberFilter}</span>
    </>
  );
};

const renderExportPanel = (errorWarningAction, errorWarningActionData) => {
  if (errorWarningAction !== ErrorWarningAction.EXPORT) {
    return null;
  }

  return (
    <ExportPanel
      list={errorWarningActionData.errorsWarnings}
      filename={`ExportedErrorsAndWarnings-${moment.utc(new Date()).format('YYYY-MM-DD')}.csv`}
    />
  );
};

const renderBulkReplacePanel = (errorWarningAction, errorWarningActionData, nodeWizardNodes, onModalClose, onApply) => {
  if (errorWarningAction !== ErrorWarningAction.BULK_FIND_AND_REPLACE) {
    return null;
  }

  return (
    <BulkReplacePanel
      errorList={errorWarningActionData.errorsWarnings}
      filename={`ExportedErrorsAndWarnings-${moment.utc(new Date()).format('YYYY-MM-DD')}.csv`}
      nodeList={valueOrDefault(nodeWizardNodes, [])}
      onCancel={onModalClose}
      onApply={onApply}
    />
  );
};

const renderErrorDetailsModal = (showModal, errorDetails, onModalClose, onSubmit, onProcessed) => {
  if (!showModal) {
    return null;
  }

  return (
    <ErrorDetailsModal
      errorDetails={errorDetails}
      open={showModal}
      onClose={onModalClose}
      onSubmit={onSubmit}
      onProcessed={onProcessed}
    />
  );
};

const renderRescheduleModal = (showModal, onModalClose, onSubmit, title, showMultiRequestWarning, languages) => {
  if (!showModal) {
    return null;
  }

  return (
    <Modal
      classNames={RESCHEDULE_MODAL_CLASSNAMES}
      open={showModal}
      onClose={onModalClose}
    >
      {
        showMultiRequestWarning && <s-alert status="warning" nodismiss="" className="mt30">
          <div>
            {LANGUAGES_CONTROLLER.formatUtilFormatter(
              languages.data,
              "errorsWarningsPage_MaxRecordsMultiRequestLimit",
              [ErrorsWarningsConfig.MAX_PROCESSING_MULTI_REQUEST_LIMIT]
            )}
          </div>
        </s-alert>
      }
      <ReschedulePanel
        onClose={onModalClose}
        onSubmit={onSubmit}
        title={title}
      />
    </Modal>
  );
};

class ExistingErrorsWarnings extends React.Component {
    constructor(props) {
        super(props);
        const categoryType = (props.subCategoryInfo && props.subCategoryInfo.type) || '';
        //Local state
        this.state = {
            data: [],
            filingTypeEnabledForRescheduleCorrection: props.filingTypeEnabledForRescheduleCorrection,
            subCategoryInfo: props.subCategoryInfo,
            selection: new Map(),
            selectAll: false,
            selectType: "checkbox",
            errorWarningActionData: {},
            selectedAction: 'select',
            viewTableState: {},
            pagination: {
                pageNumber: 1,
                pageSize: 20
            },
            loading: false,
            showErrorWarningActionModal: false,
            showErrorDetailsModal: false,
            showRescheduleCorrectivesModal: false,
            showRescheduleNonCorrectivesModal: false,
            showEnterCreditNoteCertificateDateModal: false,
            selectedErrorIdForRescheduleCorrection: null,
            selectedErrorIdForRescheduleNonCorrection: null,
            showMultiRequestWarning: false,
            disableActions: categoryType === ErrorKind.ERROR ? DEFAULT_ERROR_DISABLE_ACTIONS: DEFAULT_WARNING_DISABLE_ACTIONS
        };

        //Function with component binding
        this.getErrorWarningsCategoryEnumByLanguage = this.getErrorWarningsCategoryEnumByLanguage.bind(this);
        this.getPages = this.getPages.bind(this);
        this.toggleSelection = this.toggleSelection.bind(this);
        this.toggleAll= this.toggleAll.bind(this);
        this.isSelected = this.isSelected.bind(this);
        this.actionClickHandler = this.actionClickHandler.bind(this);
        this.errorWarningActionFindReplaceHandler = this.errorWarningActionFindReplaceHandler.bind(this);
        this.errorWarningActionExportHandler = this.errorWarningActionExportHandler.bind(this);
        this.errorWarningActionHandler = this.errorWarningActionHandler.bind(this);
        this.openErrorWarningActionModal = this.openErrorWarningActionModal.bind(this);
        this.closeErrorWarningActionModal = this.closeErrorWarningActionModal.bind(this);
        this.openErrorDetailsModal = this.openErrorDetailsModal.bind(this);
        this.closeErrorDetailsModal = this.closeErrorDetailsModal.bind(this);
        this.mapErrorWarningIdsToData = this.mapErrorWarningIdsToData.bind(this);
        this.onErrorWarningActionModalClose = this.onErrorWarningActionModalClose.bind(this);
        this.onBulkReplacePanelApply = this.onBulkReplacePanelApply.bind(this);
        this.onFetchDataHandler = this.onFetchDataHandler.bind(this);
        this.resetPageDefaults = this.resetPageDefaults.bind(this);
        this.getFilterSettingForAllAction = this.getFilterSettingForAllAction.bind(this);
        this.onErrorDetailsModalClose = this.onErrorDetailsModalClose.bind(this);
        this.onErrorDetailsModalSubmit = this.onErrorDetailsModalSubmit.bind(this);
        this.loadErrors = this.loadErrors.bind(this);
        this.loadWarnings = this.loadWarnings.bind(this);
        this.onErrorDocumentProcessed = this.onErrorDocumentProcessed.bind(this);
        this.loadSubcategories = this.loadSubcategories.bind(this);
        this.postErrorWarningActionHandler = this.postErrorWarningActionHandler.bind(this);
        this.unloadErrorsWarnings = this.unloadErrorsWarnings.bind(this);
        this.onDisabledActionsChanged = this.onDisabledActionsChanged.bind(this);
        this.openRescheduleCorrectivesModal = this.openRescheduleCorrectivesModal.bind(this);
        this.closeRescheduleCorrectivesModal = this.closeRescheduleCorrectivesModal.bind(this);
        this.submitRescheduleCorrectives = this.submitRescheduleCorrectives.bind(this);
        this.openRescheduleNonCorrectivesModal = this.openRescheduleNonCorrectivesModal.bind(this);
        this.closeRescheduleNonCorrectivesModal = this.closeRescheduleNonCorrectivesModal.bind(this);
        this.submitRescheduleNonCorrectives = this.submitRescheduleNonCorrectives.bind(this);
        this.openEnterCreditNoteCertificateDateModal = this.openEnterCreditNoteCertificateDateModal.bind(this);
        this.closeEnterCreditNoteCertificateDateModal = this.closeEnterCreditNoteCertificateDateModal.bind(this);
        this.submitCreditNoteCertificateDate = this.submitCreditNoteCertificateDate.bind(this);
    }

    onDisabledActionsChanged(disabledActions) {
        this.setState({ disableActions: disabledActions });
    }

    openErrorDetailsModal() {
        this.setState({
            showErrorDetailsModal: true
        });
    }

    closeErrorDetailsModal() {
        this.setState({
            showErrorDetailsModal: false
        });
    }

    openRescheduleCorrectivesModal(selectedErrorId){
        const isOutOfMultiRequestLimit = this.state.selection?.size > ErrorsWarningsConfig.MAX_PROCESSING_MULTI_REQUEST_LIMIT;
        this.setState({
            selectedErrorIdForRescheduleCorrection : selectedErrorId,
            showRescheduleCorrectivesModal: true,
            showMultiRequestWarning: rescheduleAllowedForReturnType(this.props.selectedFilingType, this.props.featureFlags) && isOutOfMultiRequestLimit
        });
    }

    openRescheduleNonCorrectivesModal(selectedErrorId){
        const isOutOfMultiRequestLimit = this.state.selection?.size > ErrorsWarningsConfig.MAX_PROCESSING_MULTI_REQUEST_LIMIT;
        this.setState({
            selectedErrorIdForRescheduleNonCorrection : selectedErrorId,
            showRescheduleNonCorrectivesModal: true,
            showMultiRequestWarning: (getNonCorrectiveTypes().includes(this.props.selectedFilingType) || this.props.selectedFilingType === FilingTypeEnum.VATRETURN) && isOutOfMultiRequestLimit
        });
    }

    submitRescheduleCorrectivesV3(newTransactionDate) {
        let selectedErrors = this.state.selection?.keys().toArray?.();
        if(rescheduleAllowedForReturnType(this.props.selectedFilingType, this.props.featureFlags)) {
            selectedErrors = selectedErrors?.splice(0, ErrorsWarningsConfig.MAX_PROCESSING_MULTI_REQUEST_LIMIT);
        }
        this.props.doRescheduleCorrectionV3(selectedErrors, newTransactionDate, () => {
            this.resetPageDefaults();
            this.postErrorWarningActionHandler();
        }, (error) => {
            if (error && error.statusText === INVALID_RESCHEDULE_PERIOD_STATUS) {
                log.setDictionaryMessage(ALL_MESSAGES.INVALID_CORRECTION_PERIOD);
            } else {
                log.setDictionaryMessage(ALL_MESSAGES.FAILED_RESCHEDULE_CORRECTION);
            }
        });
    }

    submitRescheduleCorrectives(newTransactionDate){
        if(rescheduleAllowedForReturnType(this.props.selectedFilingType, this.props.featureFlags)) {
            this.submitRescheduleCorrectivesV3(newTransactionDate);
        } else {
            this.props.doRescheduleCorrection(this.state.selectedErrorIdForRescheduleCorrection, newTransactionDate, () => {
                this.resetPageDefaults();
                this.postErrorWarningActionHandler();
            }, (error) => {
                if(error && error.statusText === INVALID_RESCHEDULE_PERIOD_STATUS)
                {
                    log.setDictionaryMessage(ALL_MESSAGES.INVALID_CORRECTION_PERIOD);
                }else{
                    log.setDictionaryMessage(ALL_MESSAGES.FAILED_RESCHEDULE_CORRECTION);
                }
            });
        }
        this.closeRescheduleCorrectivesModal();
    }

    submitRescheduleNonCorrectivesLatest(newTransactionDate) {
      let selectedErrors = this.state.selection?.keys().toArray?.();
      if (getNonCorrectiveTypes().includes(this.props.selectedFilingType)) {
        selectedErrors = selectedErrors?.splice(0, ErrorsWarningsConfig.MAX_PROCESSING_MULTI_REQUEST_LIMIT);
      }
      this.props.doRescheduleNonCorrectionLatest(selectedErrors, newTransactionDate, () => {
        this.resetPageDefaults();
        this.postErrorWarningActionHandler();
      }, (error) => {
          if (error && error.statusText === INVALID_RESCHEDULE_PERIOD_STATUS) {
              log.setDictionaryMessage(ALL_MESSAGES.INVALID_NON_CORRECTION_PERIOD);
          } else {
              log.setDictionaryMessage(ALL_MESSAGES.FAILED_RESCHEDULE_NON_CORRECTION);
          }
      });
    }

    submitRescheduleNonCorrectives(newTransactionDate) {
      if (getNonCorrectiveTypes().includes(this.props.selectedFilingType)) {
        this.submitRescheduleNonCorrectivesLatest(newTransactionDate);
      } else {
        this.props.doRescheduleNonCorrection(this.state.selectedErrorIdForRescheduleNonCorrection, newTransactionDate, () => {
          this.resetPageDefaults();
          this.postErrorWarningActionHandler();
        }, (error) => {
          if (error && error.statusText === INVALID_RESCHEDULE_PERIOD_STATUS) {
            log.setDictionaryMessage(ALL_MESSAGES.INVALID_NON_CORRECTION_PERIOD);
          } else {
            log.setDictionaryMessage(ALL_MESSAGES.FAILED_RESCHEDULE_NON_CORRECTION);
          }
        });
      }
      this.closeRescheduleNonCorrectivesModal();
    }

    closeRescheduleCorrectivesModal(){
        this.setState({
            showRescheduleCorrectivesModal: false,
            selectedErrorIdForRescheduleCorrection : null,
            selectedAction: 'select',
            showMultiRequestWarning: false
        });
    }

    closeRescheduleNonCorrectivesModal(){
        this.setState({
            showRescheduleNonCorrectivesModal: false,
            selectedErrorIdForRescheduleNonCorrection : null,
            selectedAction: 'select'
        });
    }

    openEnterCreditNoteCertificateDateModal() {
      const isOutOfMultiRequestLimit = this.state.selection?.size > ErrorsWarningsConfig.MAX_PROCESSING_MULTI_REQUEST_LIMIT;
      this.setState({
        showEnterCreditNoteCertificateDateModal: true,
        showMultiRequestWarning: isOutOfMultiRequestLimit
      });
    }

    closeEnterCreditNoteCertificateDateModal() {
      this.setState({
        showEnterCreditNoteCertificateDateModal: false,
        selectedAction: 'select',
        showMultiRequestWarning: false,
      });
    }

    submitCreditNoteCertificateDate(newTransactionDate) {
      this.closeEnterCreditNoteCertificateDateModal();
      const selectedIds = Array.from(this.state.selection, ([key, _value]) => key)
        .splice(0, ErrorsWarningsConfig.MAX_PROCESSING_MULTI_REQUEST_LIMIT);

      this.props.doEnterCreditNoteCertificateDate(
        selectedIds,
        newTransactionDate,
        () => {
          this.resetPageDefaults();
          this.postErrorWarningActionHandler();
        },
        () => {
          log.setDictionaryMessage(ALL_MESSAGES.FAILED_CREDIT_NOTE_CERTIFICATE_SUBMISSION);
        }
      );
    }

    getErrorWarningsCategoryEnumByLanguage (value) {
        return ErrorWarningsCategory.enumValues.find( (enumEntry) => {
          return LANGUAGES_CONTROLLER.format(this.props.languages, enumEntry.languageString ) === value;
        });
    }

    loadErrors(filterCriteria) {
        log.debug('loadErrors() - filterCriteria: ', filterCriteria);
        filterCriteria.errorCategory = this.getErrorWarningsCategoryEnumByLanguage(filterCriteria.errorCategory);
        this.props.doLoadErrors(filterCriteria);
    }

    loadWarnings(filterCriteria) {
        log.debug('loadWarnings() - filterCriteria: ', filterCriteria);
        filterCriteria.errorCategory = this.getErrorWarningsCategoryEnumByLanguage(filterCriteria.errorCategory);
        this.props.doLoadWarnings(filterCriteria);
    }

    onErrorDetailsModalSubmit(){
        this.closeErrorDetailsModal();
        this.setState({
            loading: true
        });
    }

    onErrorDocumentProcessed() {
        log.debug('onErrorDocumentProcessed() ');
        const errorKind = (this.state.subCategoryInfo && this.state.subCategoryInfo.type) || '';
        this.resetPageDefaults();
        this.postErrorWarningActionHandler();
        if(typeof this.props.postErrorWarningAction === 'function') {
            this.props.postErrorWarningAction(this.state.viewTableState, errorKind);
        }
    }

    onErrorDetailsModalClose() {
        log.debug('onErrorDetailsModalClose() ');
        this.resetPageDefaults();
        this.closeErrorDetailsModal();
    }

    openErrorWarningActionModal() {
        log.debug('openErrorWarningActionModal()');
        this.setState({
            showErrorWarningActionModal: true
        });
    }

    closeErrorWarningActionModal() {
        log.debug('closeErrorWarningActionModal()');
        this.setState({
            showErrorWarningActionModal: false
        });
    }

    resetPageDefaults() {
        log.debug('resetPageDefaults()');
        this.setState({
            errorWarningActionData: {},
            selection: new Map(),
            selectAll: false,
            selectedAction: 'select'
        });
    }

    onErrorWarningActionModalClose() {
        log.debug('onErrorWarningActionModalClose()');
        this.resetPageDefaults();
        this.closeErrorWarningActionModal();
    }

    onBulkReplacePanelApply(errorIds, replaceTagValues) {
        let errorWarningActionData = this.state.errorWarningActionData || {};
        this.setState({
            loading: true
        });
        if(this.state.selectAll) {
            let filterCriteria = errorWarningActionData.filterCriteria;
            log.debug('onBulkReplacePanelApply(): ', errorWarningActionData.action, filterCriteria);
            this.props.doActionOnFilters(ErrorWarningAction.ERROR_ACTION_MOVE.value, filterCriteria, replaceTagValues, (errors, successes) => {
                log.debug('onBulkReplacePanelApply() -> doActionOnFilters() -> callback(): ', errors, successes);
                this.onErrorWarningActionModalClose();
                this.postErrorWarningActionHandler();
                if(typeof this.props.postErrorWarningAction === 'function') {
                    this.props.postErrorWarningAction(this.state.viewTableState, errorWarningActionData.type, errorWarningActionData.action);
                }
            });
        } else {
            log.debug('onBulkReplacePanelApply() - errorIds: %s, replaceTagValues: %s', errorIds, replaceTagValues);
            this.props.doApplyErrorsActions(errorIds, replaceTagValues, (errors, data)=>{
                log.debug('onBulkReplacePanelApply() -> doApplyErrorsActions() -> callback(): ', errors, data);
                this.onErrorWarningActionModalClose();
                this.postErrorWarningActionHandler();
                if(typeof this.props.postErrorWarningAction === 'function') {
                    this.props.postErrorWarningAction(this.state.viewTableState, errorWarningActionData.type, errorWarningActionData.action);
                }
            });
        }
    }

    mapErrorWarningIdsToData(idsToAction, _type) {
        if(_.isEmpty(idsToAction) || _.isEmpty(this.state.data)) {
            return [];
        }
        return this.state.data.filter( entry => idsToAction.has(entry.id));
    }

    toggleSelection(key, shift, row){
        log.debug('toggleSelection(): ', key, row);
        /*
          Implementation of how to manage the selection state is up to the developer.
          This implementation uses an array stored in the component state.
          Other implementations could use object keys, a Javascript Set, or Redux... etc.
        */
        // start off with the existing state
        let selection = new Map(this.state.selection);
        let aliasKey =  `${key}`.includes('select-') ? `${key}`.replace('select-','') : `select-${key}`;
        // check to see if the key exists
        if (selection.has(key) || selection.has(aliasKey) || selection.has(parseInt(aliasKey, 10))) {
          // it does exist so we will remove it using destructing
          selection.delete(key);
        } else {
          // it does not exist so add it
          selection.set(key);
        }

        //Reset select all if any 1 or more records are not selected
        let selectAll = this.state.selectAll;
        if(this.state.selectAll && (selection.size !== this.state.data.length)) {
            selectAll = false;
        }
        // update the state
        var checkedErrorRow = this.state.data.find(x => x.id === selection.keys().next().value);
        this.setState({ selection: selection, selectAll: selectAll, errorCodeId: checkedErrorRow && checkedErrorRow.codeId });
    }

    toggleAll() {
        log.debug('toggleAll(): ');
        const selectAll = !this.state.selectAll;
        const selection = new Map();
        if (selectAll) {
            // we need to get at the internals of ReactTable
            const wrappedInstance = this.viewTable.getWrappedInstance();
            // the 'sortedData' property contains the currently accessible records based on the filter and sort
            const currentRecords = wrappedInstance.getResolvedState().sortedData;
            // we just push all the IDs onto the selection array
            currentRecords.forEach(item => {
                if (item._original) {
                    selection.set(item._original.id);
                }
            });
        }
        log.debug('toggleAll(): selectAll: ', selectAll);
        log.debug('toggleAll(): selection: ', selection);
        this.setState({ selectAll: selectAll, selection: selection, errorCodeId :undefined });
    }

    isSelected(key) {
        if(this.state.selection.has(key)) {
            return true;
        }
        else if(this.state.selection.has(parseInt(key, 10))) {
            return true;
        }
        else if(this.state.selection.has(`${key}`.replace('select-',''))
        || this.state.selection.has(parseInt(`${key}`.replace('select-',''), 10))) {
            return true;
        }
        else if(this.state.selection.has(`select-${key}`)) {
            return true;
        }
        return false;
    }

    getFilterSettingForAllAction(subCategoryInfo={}) {
        const data = subCategoryInfo.data || {};
        const type = subCategoryInfo.type;
        const companyId = (data.company && data.company.id) || 0;
        const filterCriteria = (data.filterCriteria) || {};
        return {
            "companyId": companyId,
            "countryCode": filterCriteria.countryCode,
            "startDate": filterCriteria.startDate,
            "endDate": filterCriteria.endDate,
            "maxNrOfErrors": 0,
            "pageNumber": 0,
            "errorCategory": (filterCriteria.errorCategory && filterCriteria.errorCategory.value) || 2,
            "includeHidden": filterCriteria.includeHidden,
            "groupBy": 1,
            "errorTreeLevel": 3,
            "includeErrors": type === ErrorKind.ERROR,
            "includeWarnings": type === ErrorKind.WARNING,
            "errorType": data.codeId,
            "subType": data.subTypeSearchValue,
            "invoiceNumberFilter": filterCriteria.invoiceNumberFilter
        };
    }

    actionClickHandler(action, rowInfo={}) {
        log.debug('actionClickHandler(): ', action, rowInfo);
        return (event) => {
            log.debug('actionClickHandler():callback(): ', event);
            switch(action) {
                case ROW_ACTIONS.VIEW_AND_RESOLVE:
                    const categoryType = (this.state.subCategoryInfo && this.state.subCategoryInfo.type) || '';
                    if(categoryType === ErrorKind.ERROR) {
                        const errorId = rowInfo.original && rowInfo.original.id;
                        this.props.doGetErrorDetail(errorId, (error, data)=>{
                            log.debug('actionClickHandler() -> doGetErrorDetail() -> callback(): ', error, data);
                            this.openErrorDetailsModal();
                        }, this.props.featureFlags);
                    }
                    break;
                default:
                    return;
            }
            if(typeof this.onActionClick === 'function') {
                this.onActionClick(action, rowInfo, event);
            }
        }
    }

    loadSubcategories(original, type, cb=()=>{}) {
        let parsedQueryParams = this.props.parsedQueryParams;
        let globalFilterValues = GLOBAL_FILTER_CONTROLLER.getAll();
        let filterCriteria = Object.assign({}, parsedQueryParams, globalFilterValues, {
            errorCodeId: original.codeId,
            subType: original.subTypeSearchValue,
            errorCategory: original.errorCategory
        });
        if(type === ErrorKind.ERROR) {
            this.props.doLoadErrorSubCategories(filterCriteria, (error, data) => {
                cb(error, data);
            });
        } else if(type === ErrorKind.WARNING) {
            this.props.doLoadWarningSubCategories (filterCriteria, (error, data) => {
                cb(error, data);
            });
        }
    }

    unloadErrorsWarnings() {
        log.debug('unloadErrorsWarnings()');
        this.props.doUnload('transactionErrors');
        this.props.doUnload('transactionWarnings');
    }

    postErrorWarningActionHandler() {
        log.debug('postErrorWarningActionHandler(): ');
        // Once the action is done we need to reload the sub-category while keeping ther error list modal open
        const errorKind = (this.state.subCategoryInfo && this.state.subCategoryInfo.type) || '';
        let subCategoryData = (this.state.subCategoryInfo && this.state.subCategoryInfo.data) || {};
        let subCategoryRow = (this.state.subCategoryInfo && this.state.subCategoryInfo.row) || {};
        this.onFetchDataHandler(this.state.viewTableState);
        this.loadSubcategories(subCategoryData, errorKind, (error, response=[]) => {
            let updatedSubCategoryInfo = Object.assign({}, this.state.subCategoryInfo);
            updatedSubCategoryInfo.data = Object.assign({}, updatedSubCategoryInfo.data, response[subCategoryRow.index]);
            this.setState({
                subCategoryInfo: updatedSubCategoryInfo,
                loading: false
            });
        });
    }

    errorWarningActionFindReplaceHandler(type, filterCriteria, idsToAction) {
      this.props.doLoadWizardNodes();
      const errorWarningActionData = {
        action: ErrorWarningAction.BULK_FIND_AND_REPLACE,
        type: type
      };

      if(this.state.selectAll) {
        errorWarningActionData.filterCriteria = filterCriteria;
      } else {
        errorWarningActionData.ids = idsToAction;
        errorWarningActionData.errorsWarnings = this.mapErrorWarningIdsToData(idsToAction, type);
      }

      this.setState({ errorWarningActionData: errorWarningActionData });
      this.openErrorWarningActionModal();
    }

    errorWarningActionExportHandler(type, idsToAction) {
      const errorWarningActionData = {
        action: ErrorWarningAction.EXPORT,
        type: type,
      };

      if(this.state.selectAll) {
        const subCategoryData = valueOrDefault((this.state.subCategoryInfo && this.state.subCategoryInfo.data), {});
        const filterCriteria = valueOrDefault(subCategoryData.filterCriteria, {});
        let apiParams = {
          companyId: subCategoryData.company ? subCategoryData.company.id : 0,
          countryCode: valueOrDefault(filterCriteria.countryCode, ''),
          endDate: valueOrDefault(filterCriteria.endDate, ''),
          startDate: valueOrDefault(filterCriteria.startDate, ''),
          subType: subCategoryData.subTypeSearchValue,
          errorCategory: subCategoryData.errorCategory,
          errorCodeId: subCategoryData.codeId,
          maxErrorCount: 0,
          pageNumber: 0
        };

        this.setState({
          loading: true
        });

        type === ErrorKind.ERROR ?
          this.props.doDownloadErrors(apiParams, () => {
            this.resetPageDefaults();
            this.setState({
              loading: false
            });
          }) :
          this.props.doDownloadWarnings(apiParams, () => {
            this.resetPageDefaults();
            this.setState({
              loading: false
            });
          });

        errorWarningActionData.filterCriteria = apiParams;
        this.setState({ errorWarningActionData: errorWarningActionData });
      } else {
        errorWarningActionData.errorsWarnings = this.mapErrorWarningIdsToData(idsToAction, type);
        this.setState({ errorWarningActionData: errorWarningActionData });
        this.openErrorWarningActionModal();
      }
    }

    errorWarningActionOnFiltersHandler(requestedErrorWarningAction, filterCriteria, type, idsToAction) {
      this.setState({
        loading: true
      });

      if(this.state.selectAll) {
        log.debug('errorWarningActionHandler(): ', requestedErrorWarningAction, filterCriteria);
        this.props.doActionOnFilters(requestedErrorWarningAction.value, filterCriteria, [], (errors, successes) => {
          log.debug('errorWarningActionHandler() -> doActionOnFilters() -> callback(): ', errors, successes);
          this.resetPageDefaults();
          this.postErrorWarningActionHandler();
          if(typeof this.props.postErrorWarningAction === 'function') {
            this.props.postErrorWarningAction(this.state.viewTableState, type, requestedErrorWarningAction);
          }
        });
      } else {
        log.debug('errorWarningActionHandler(): ', requestedErrorWarningAction, idsToAction);
        this.props.doActionOnIds(requestedErrorWarningAction.value, [...idsToAction.keys()], (errors, successes) => {
          log.debug('errorWarningActionHandler() -> doActionOnIds() -> callback(): ', errors, successes);
          this.resetPageDefaults();
          this.postErrorWarningActionHandler();
          if(typeof this.props.postErrorWarningAction === 'function') {
            this.props.postErrorWarningAction(this.state.viewTableState, type, requestedErrorWarningAction);
          }
        });
      }
    }

    errorWarningActionHandler(requestedErrorWarningAction) {
      log.debug('errorWarningActionHandler(): ', requestedErrorWarningAction);
      const subCategoryInfo = valueOrDefault(this.state.subCategoryInfo, {});
      const type = subCategoryInfo.type;
      const filterCriteria = this.getFilterSettingForAllAction(subCategoryInfo);
      let idsToAction = valueOrDefault(this.state.selection, new Map());

      switch(requestedErrorWarningAction) {
        case ErrorWarningAction.REOPEN_AND_REDELIVER:
          this.props.openCorrectivesModal();
          break;
        case ErrorWarningAction.BULK_FIND_AND_REPLACE:
          this.errorWarningActionFindReplaceHandler(type, filterCriteria, idsToAction);
          break;
        case ErrorWarningAction.EXPORT:
          this.errorWarningActionExportHandler(type, idsToAction);
          break;
        case ErrorWarningAction.ERROR_ACTION_VATRATE:
        case ErrorWarningAction.ERROR_ACTION_INVNR:
        case ErrorWarningAction.ERROR_ACTION_SUPINVNR:
        case ErrorWarningAction.ERROR_ACTION_VATNR:
        case ErrorWarningAction.ERROR_ACTION_MOVE:
        case ErrorWarningAction.ERROR_ACTION_DELETE:
        case ErrorWarningAction.ERROR_ACTION_MOVE_WARNING_TO_ERROR:
        case ErrorWarningAction.ERROR_ACTION_SAVE_TO_FILE:
        case ErrorWarningAction.ERROR_ACTION_OSS_VATNR:
          this.errorWarningActionOnFiltersHandler(requestedErrorWarningAction, filterCriteria, type, idsToAction);
          break;
        case ErrorWarningAction.RESCHEDULE_AS_CORRECTIVE:
          let selectedErrorId = this.state.selection.keys().next().value;
          log.clearAllDictionaryErrors();
          this.openRescheduleCorrectivesModal(selectedErrorId);
          break;
        case ErrorWarningAction.RESCHEDULE_AS_NONCORRECTIVE:
          let errorId = this.state.selection.keys().next().value;
          log.clearAllDictionaryErrors();
          this.openRescheduleNonCorrectivesModal(errorId);
          break;
        case ErrorWarningAction.ERROR_ACTION_ENTER_CREDIT_NOTE_CERTIFICATE_DATE:
          if (!this.props.featureFlags?.[CREDIT_NOTE_FF]) {
            break;
          }

          log.clearAllDictionaryErrors();
          this.openEnterCreditNoteCertificateDateModal();
          break;
        default:
          this.setState({
            errorWarningActionData: {},
            loading: false
          });

          log.clearAllDictionaryErrors();
          return;
      }

      this.setState({
        selectedAction: requestedErrorWarningAction ? requestedErrorWarningAction.value : 'select'
      });

      if(typeof this.props.onErrorWarningActionClick === 'function') {
        this.props.onErrorWarningActionClick(
          requestedErrorWarningAction,
          idsToAction,
          this.state.selectAll,
          type
        );
      }
    }

    getPages(totalCount, pageSize=20) {
        if(totalCount < 1 || pageSize < 1) {
            return 1;
        }
        let pages = Math.ceil(totalCount / pageSize);
        return  pages < 1 ? 1: pages;
    }

    onFetchDataHandler(state) {
        log.debug('onFetchDataHandler(): ');
        let { page, pageSize } = state;
        pageSize = Object.is(pageSize, NaN) ? 20 : pageSize;
        page = Object.is(page, NaN) ? 0 : page;
        let pagination = Object.assign({}, this.state.pagination,  {
            pageNumber: page + 1,
            pageSize: pageSize
        });
        this.setState({
            pagination: pagination,
            viewTableState: state
        });
        let parsedQueryParams = this.props.parsedQueryParams;
        let globalFilterValues = GLOBAL_FILTER_CONTROLLER.getAll();
        let selectedSubCategoryInfo = this.state.subCategoryInfo;
        if(_.isEmpty(selectedSubCategoryInfo) || _.isEmpty(selectedSubCategoryInfo.type) || selectedSubCategoryInfo.type === ErrorKind.UNKNOWN) {
            return;
        }
        let {type, data={}} = selectedSubCategoryInfo;
        let filterCriteria = Object.assign({}, parsedQueryParams, globalFilterValues, {
                companyId: data.company ? data.company.id : 0,
                invoiceNumberFilter: this.state.subCategoryInfo.data.filterCriteria.invoiceNumberFilter,
                errorCodeId: data.codeId,
                subType: data.subTypeSearchValue,
                errorCategory: data.errorCategory,
                pageNumber: pagination.pageNumber,
                maxErrorCount: pagination.pageSize
            });
        if(type === ErrorKind.ERROR) {
            this.loadErrors(filterCriteria);
        } else if(type === ErrorKind.WARNING) {
            this.loadWarnings(filterCriteria);
        }
    }

    defaultSelectInputComponent() {
        return SelectInputComponent;
    }

    render() {
      log.debug('render(): ', this.props);
      const data = valueOrDefault(this.state.data, []);
      const errorKind = valueOrDefault((this.state.subCategoryInfo?.type), '');
      const subCategoryData = valueOrDefault((this.state.subCategoryInfo?.data), {});
      const filterCriteria = valueOrDefault(subCategoryData.filterCriteria, {});
      const companyName = valueOrDefault((subCategoryData.company?.name), '');
      const pagination = valueOrDefault(this.state.pagination, {});
      const showMaxRecordsWarning = shouldShowMaxRecordWarning(
        this.state.selectAll,
        subCategoryData.totalItemsCount,
        subCategoryData.codeId,
        this.props.selectedFilingType,
        this.props.featureFlags
      );
      const disableActionPanel = valueOrDefault(_.isEmpty(this.state.selection), showMaxRecordsWarning);
      const errorWarningActionData = valueOrDefault(this.state.errorWarningActionData, {});
      const errorWarningAction = errorWarningActionData.action;
      const columns = [
        {
          Header:  getMessageHeaderRenderer(this.props.languages, subCategoryData),
          accessor: 'message',
          Cell: getMessageRowRenderer(this.props.languages),
        },
        {
          accessor: 'actions',
          width: 100,
          Cell: getActionsRowRenderer(errorKind, this.actionClickHandler, this.props.languages),
        },
      ];

      const checkboxProps = {
        selectAll: this.state.selectAll,
        isSelected: this.isSelected,
        toggleAll: this.toggleAll,
        toggleSelection: this.toggleSelection,
        selectType: "checkbox",
        SelectInputComponent: this.defaultSelectInputComponent(),
        SelectAllInputComponent: this.defaultSelectInputComponent(),
      };

      const getErrorKindLabel = () => {
        if (errorKind === ErrorKind.ERROR) {
          return LANGUAGES_CONTROLLER.format(this.props.languages, 'existingCategories_Errors').toLowerCase();
        }

        if (errorKind === ErrorKind.WARNING) {
          return LANGUAGES_CONTROLLER.format(this.props.languages, 'existingCategories_Warnings').toLowerCase();
        }

        return null;
      }

      return(
        <div className="mb20">
          <h2>
            { _.isEmpty(companyName) ? '' : `${companyName}: `}
            {`${LANGUAGES_CONTROLLER.format(this.props.languages, 'existingErrorsWarnings_Resolve')} `}
            {`"${subCategoryData.subType}" `}
            { getErrorKindLabel() }
          </h2>
          <ApplicationMessages scope={[FORM_NAME]} messageType="applicationError"/>
          <ApplicationMessages
            messageIds={[ALL_MESSAGES.ERRORS_WARNINGS_ACTION_RECORDS_SKIPPED]}
            messageType="applicationWarning"
          />
          {
            maxRecordsMessage(showMaxRecordsWarning, this.props.languages)
          }
          <br/>
          <div className="summary-panel">
            {
              summaryCountry(filterCriteria.countryCode, this.props.languages)
            }
            {
              summaryPeriod(filterCriteria, this.props.languages)
            }
            {
              summaryInvoiceNumber(filterCriteria, this.props.languages)
            }
          </div>
          <div className="mt20 mb20">
            <ActionsPanel
              onAction={this.errorWarningActionHandler}
              disable={valueOrDefault(this.state.disableActions, [])}
              selectedValue={this.state.selectedAction}
              disableAll={disableActionPanel}
            />
          </div>
          <SelectTable
            data={data}
            columns={columns}
            manual
            minRows={0}
            maxRows={0}
            ref={(r) => this.viewTable = r}
            keyField={this.props.keyField}
            defaultFilterMethod={(filter, row) => String(row[filter.id]).includes(filter.value)}
            {...checkboxProps}
            NoDataComponent={NoDataComponent}
            sortable={false}
            defaultPageSize={valueOrDefault(pagination.pageSize, 20)}
            pageSizeOptions={[ 20, 50, 100, 500, 1000]}
            pages={valueOrDefault(pagination.pages, 1)}
            onFetchData={this.onFetchDataHandler}
            PaginationComponent={PaginationComponent}
            showPagination={data.length > 0}
            showPageJump={false}
            loading={this.state.loading}
          />
          <div>
            <Modal
              classNames={STANDARD_MODAL_CLASSNAMES}
              open={this.state.showErrorWarningActionModal}
              onClose={this.onErrorWarningActionModalClose}
              closeOnEsc={true}
              blockScroll={true}
              closeOnOverlayClick={false}
              center
            >
              <div>
                {
                  renderExportPanel(errorWarningAction, errorWarningActionData)
                }
                {
                 renderBulkReplacePanel(
                   errorWarningAction,
                   errorWarningActionData,
                   this.props.nodeWizardNodes,
                   this.onErrorWarningActionModalClose,
                   this.onBulkReplacePanelApply
                 )
                }
              </div>
            </Modal>
          </div>
          <div>
            {
              renderErrorDetailsModal(
                this.state.showErrorDetailsModal,
                this.props.errorDetails,
                this.onErrorDetailsModalClose,
                this.onErrorDetailsModalSubmit,
                this.onErrorDocumentProcessed
              )
            }
          </div>
          <div>
            {
              // Reschedule Correctives Modal
              renderRescheduleModal(
                this.state.showRescheduleCorrectivesModal,
                this.closeRescheduleCorrectivesModal,
                this.submitRescheduleCorrectives,
                "rescheduleCorrectives_header",
                this.state.showMultiRequestWarning,
                this.props.languages
              )
            }
            {
              // Reschedule Non-Correctives Modal
              renderRescheduleModal(
                this.state.showRescheduleNonCorrectivesModal,
                this.closeRescheduleNonCorrectivesModal,
                this.submitRescheduleNonCorrectives,
                "rescheduleNonCorrectives_header",
                this.state.showMultiRequestWarning,
                this.props.languages
              )
            }
            {
              //  Enter Credit Note Certificate Date Modal
              renderRescheduleModal(
                this.state.showEnterCreditNoteCertificateDateModal,
                this.closeEnterCreditNoteCertificateDateModal,
                this.submitCreditNoteCertificateDate,
                "enterCreditNoteCertificateDate_header",
                this.state.showMultiRequestWarning,
                this.props.languages
              )
            }
          </div>
          <br/>
          <DisabledActions
            disabledActionsChanged={this.onDisabledActionsChanged}
            errorsSelection={this.state.selection}
            warningsSelection={this.state.selection}
            errorCodeId={subCategoryData.codeId}
            subCategoryInfo={errorKind}
            filingTypeEnabledForRescheduleCorrection={this.state.filingTypeEnabledForRescheduleCorrection}
            totalItemsCount={subCategoryData?.totalItemsCount}
            isErrorsWarningsModal={true}
            selectedFilingType={this.props.selectedFilingType}
          />
        </div>
      );
    }

    componentDidUpdate(prevProps) {
        let selectedSubCategoryInfo = this.state.subCategoryInfo;
        if(selectedSubCategoryInfo.type === ErrorKind.ERROR && (this.props.transactionErrors !== prevProps.transactionErrors)) {
            if((this.props.transactionErrors !== prevProps.transactionErrors)  && _.isEmpty(this.props.transactionErrors)) {
                this.props.onNoData();
                return;
            }
            let pagination =  Object.assign({}, this.state.pagination);
            pagination.pages = this.getPages(selectedSubCategoryInfo.data && selectedSubCategoryInfo.data.totalItemsCount, pagination.pageSize);
            this.setState({
                data: this.props.transactionErrors || [],
                pagination: pagination,
                selection: new Map(),
                selectAll: false
            });

        }
        if(selectedSubCategoryInfo.type === ErrorKind.WARNING && (this.props.transactionWarnings !== prevProps.transactionWarnings)) {
            if((this.props.transactionWarnings !== prevProps.transactionWarnings) && _.isEmpty(this.props.transactionWarnings)) {
                this.props.onNoData();
                return;
            }
            let pagination =  Object.assign({}, this.state.pagination);
            pagination.pages = this.getPages(selectedSubCategoryInfo.data && selectedSubCategoryInfo.data.totalItemsCount, pagination.pageSize);
            this.setState({
                data: this.props.transactionWarnings || [],
                pagination: pagination,
                selection: new Map(),
                selectAll: false
            });
        }
        if(this.props.subCategoryInfo !== prevProps.subCategoryInfo) {
            if(_.isEmpty(this.props.subCategoryInfo) || _.isEmpty(this.props.subCategoryInfo.type)) {
                return;
            }
            let newSubCategoryInfo = this.props.subCategoryInfo;
            const categoryType = (newSubCategoryInfo.type) || '';
            let pagination =  Object.assign({}, this.state.pagination);
            pagination.pages = this.getPages(newSubCategoryInfo.data && newSubCategoryInfo.data.totalItemsCount, pagination.pageSize);
            this.setState({
                subCategoryInfo: newSubCategoryInfo,
                disableActions: categoryType === ErrorKind.ERROR ? DEFAULT_ERROR_DISABLE_ACTIONS: DEFAULT_WARNING_DISABLE_ACTIONS,
                pagination: pagination
            });
        }
    }

    componentWillUnmount(){
        this.setState({
            data: [],
            selection: new Map(),
            selectAll: false
        });
        setTimeout(()=>{this.unloadErrorsWarnings();}, 10);
    }
}


ExistingErrorsWarnings.defaultProps = {
    data: [],
    keyField: "_id",
    subCategoryInfo: {},
    pagination: {},
    onActionClick: () => {},
    onFetchData: () => {},
    onErrorWarningActionClick: () => {},
    postErrorWarningAction: () => {},
    onNoData: ()=>{},
    showErrorListModal: false
};

function mapStateToProps(state) {
    log.debug('mapStateToProps(): ');
    let stateProps = {};
    stateProps.languages = state.languages;
    stateProps.nodeWizardNodes = _.isArray(state.errorsWarnings.nodeWizardNodes) && state.errorsWarnings.nodeWizardNodes.map((n) => n.nodeName);
    stateProps.errorDetails = state.errorDetail && state.errorDetail.data;
    stateProps.reopenableClosedReturns = state.errorsWarnings.reopenableClosedReturns;
    stateProps.transactionErrors = state.errorsWarnings.transactionErrors;
    stateProps.transactionWarnings = state.errorsWarnings.transactionWarnings;
    stateProps.featureFlags = state.featureFlag && state.featureFlag.data;
    log.debug('mapStateToProps(): stateProps: ', stateProps);
    return stateProps;
}

function mapDispatchersToProps(dispatch) {
    log.debug('mapDispatchersToProps(): ');
    let dispatcherProps = {};

    dispatcherProps.doLoadWizardNodes = () => {
        log.debug('doLoadWizardNodes()');
        dispatch(ERRORSWARNINGS_CONTROLLER.doLoadNodeWizardNodes());
    };

    dispatcherProps.doGetReopenableClosedReturns = () => {
        log.debug('doGetReopenableClosedReturns()');
        dispatch(ERRORSWARNINGS_CONTROLLER.doGetReopenableClosedReturns());
    };

    dispatcherProps.doApplyErrorsActions = (ids, replaceTagValues, callback=()=>{}) => {
        log.debug('doApplyErrorsActions(): ', ids, replaceTagValues)
        dispatch(ERRORSWARNINGS_CONTROLLER.doActionOnIds(ErrorWarningAction.ERROR_ACTION_MOVE.value, ids, replaceTagValues, callback));
    };

    dispatcherProps.doActionOnIds = (action, ids=[], callback=()=>{}) => {
        log.debug('doActionOnIds(): ', action, ids);
        dispatch(ERRORSWARNINGS_CONTROLLER.doActionOnIds(action, ids, [], callback));
    };

    dispatcherProps.doActionOnFilters = (action, filter={}, replaceTagValues=[], callback=()=>{}) => {
        log.debug('doActionOnFilters(): ', action, filter);
        dispatch(ERRORSWARNINGS_CONTROLLER.doActionOnFilter(action, filter, replaceTagValues, callback));
    };

    dispatcherProps.doGetErrorDetail = (id, callback=()=>{}, featureFlags = []) => {
        log.debug('doGetErrorDetail(): ', id);
        dispatch(ERROR_DOCUMENT_DETAIL_CONTROLLER.doLoad(id, callback, featureFlags));
    }

    dispatcherProps.doUnloadErrorDetail = (callback=()=>{}) => {
        log.debug('doUnloadErrorDetail(): ');
        dispatch(ERROR_DOCUMENT_DETAIL_CONTROLLER.doUnLoad(callback));
    }

    dispatcherProps.doDownloadErrors = (filterCriteria={}, callback=()=>{}) => {
        log.debug('doDownloadErrors(): ');
        dispatch(ERRORSWARNINGS_CONTROLLER.doDownloadErrors(filterCriteria, callback));
    }

    dispatcherProps.doDownloadWarnings = (filterCriteria={}, callback=()=>{}) => {
        log.debug('doDownloadWarnings(): ');
        dispatch(ERRORSWARNINGS_CONTROLLER.doDownloadWarnings(filterCriteria, callback));
    }

    dispatcherProps.doUnload = (dataName='') => {
        log.debug('doUnload()');
        dispatch(ERRORSWARNINGS_CONTROLLER.doUnload(dataName));
    };

    dispatcherProps.doLoadErrors = (filterParams) => {
        log.debug('doLoadErrors: ', filterParams);
        const modifiedFilterParams = Object.assign({}, filterParams, {
          includeErrors: undefined,
          includeWarnings: undefined,
          companyCode: undefined,
          errorCategory: filterParams.errorCategory ? filterParams.errorCategory.value : ErrorWarningsCategory.ALL_ERRORS.value
        });
        log.debug('doLoadErrors: ', modifiedFilterParams);
        dispatch(ERRORSWARNINGS_CONTROLLER.doLoadErrors(modifiedFilterParams));
    };

    dispatcherProps.doLoadWarnings = (filterParams) => {
        log.debug('doLoadWarnings: ', filterParams);
        const modifiedFilterParams = Object.assign({}, filterParams, {
          includeErrors: undefined,
          includeWarnings: undefined,
          errorCategory: filterParams.errorCategory ? filterParams.errorCategory.value : ErrorWarningsCategory.ALL_ERRORS.value
        });
        log.debug('doLoadWarnings: ', modifiedFilterParams);
        dispatch(ERRORSWARNINGS_CONTROLLER.doLoadWarnings(modifiedFilterParams));
    };

    dispatcherProps.doLoadErrorSubCategories = (filterParams={}, callback=()=>{}) => {
        log.debug('doLoadErrorSubCategories() : filterParams: ', filterParams);
        const modifiedFilterParams = Object.assign({}, filterParams, {
            includeErrors: undefined,
            includeWarnings: undefined,
            companyId: (typeof filterParams.companyId === 'undefined' || filterParams.companyId === 'All') ? 0 : filterParams.companyId
        });
        log.debug('doLoadErrorSubCategories: ', modifiedFilterParams);
        dispatch(ERRORSWARNINGS_CONTROLLER.doLoadErrorSubCategories(modifiedFilterParams, callback));
    };

    dispatcherProps.doLoadWarningSubCategories = (filterParams={}, callback=()=>{}) => {
        log.debug('doLoadWarningSubCategories() : filterParams: ', filterParams);
        const modifiedFilterParams = Object.assign({}, filterParams, {
            includeErrors: undefined,
            includeWarnings: undefined,
            companyId: (typeof filterParams.companyId === 'undefined' || filterParams.companyId === 'All') ? 0 : filterParams.companyId
        });
        log.debug('doLoadWarningSubCategories: ', modifiedFilterParams);
        dispatch(ERRORSWARNINGS_CONTROLLER.doLoadWarningSubCategories(modifiedFilterParams, callback));
    };

    dispatcherProps.doRescheduleCorrection = (errorId, newTransactionDate, successHandler, errorHandler) => {
        dispatch(ERRORSWARNINGS_CONTROLLER.doRescheduleCorrection(errorId, newTransactionDate, successHandler, errorHandler));
    }

    dispatcherProps.doRescheduleCorrectionV3 = (errorIds, newTransactionDate, successHandler, errorHandler) => {
        dispatch(ERRORSWARNINGS_CONTROLLER.doRescheduleCorrectionV3(errorIds, newTransactionDate, successHandler, errorHandler));
    }

    dispatcherProps.doRescheduleNonCorrection = (errorId, newTransactionDate, successHandler, errorHandler) => {
        dispatch(ERRORSWARNINGS_CONTROLLER.doRescheduleNonCorrection(errorId, newTransactionDate, successHandler, errorHandler));
    }

    dispatcherProps.doRescheduleNonCorrectionLatest = (errorIds, newTransactionDate, successHandler, errorHandler) => {
      dispatch(ERRORSWARNINGS_CONTROLLER.doRescheduleNonCorrectionLatest(errorIds, newTransactionDate, successHandler, errorHandler));
  }

    dispatcherProps.doEnterCreditNoteCertificateDate = (errorIds, newTransactionDate, successHandler, errorHandler) => {
        dispatch(ERRORSWARNINGS_CONTROLLER.doEnterCreditNoteCertificateDate(
          errorIds,
          newTransactionDate,
          successHandler,
          errorHandler
        ));
    }

    log.debug('mapDispatchersToProps(): dispatcherProps: ', dispatcherProps);
    return dispatcherProps;
}

export default connect(mapStateToProps, mapDispatchersToProps)(ExistingErrorsWarnings);
